import type { TextProps } from '@chakra-ui/react';
import { Span, Text } from '@chakra-ui/react';
import Mustache from 'mustache';
import React from 'react';

// Disable HTML escaping in Mustache templates to allow HTML in templates if needed
Mustache.escape = function (text) {
  return text;
};

interface TemplatedTextProps extends Omit<TextProps, 'children'> {
  template: string;
  values: Record<string, string>;
  highlight?: boolean;
  highlightStyle?: React.CSSProperties;
}

/**
 * A component that renders text with Mustache templating
 *
 * @param template - The mustache template string
 * @param values - The values to be used for rendering
 * @param highlight - Whether to highlight the replaced values
 * @param highlightStyle - Custom style for highlighted values
 */
export const TemplatedText: React.FC<TemplatedTextProps> = ({
  template,
  values,
  highlight = false,
  highlightStyle = {
    fontWeight: 'medium',
    backgroundColor: 'var(--chakra-colors-primary-50)',
    color: 'var(--chakra-colors-primary-500)',
  },
  ...textProps
}) => {
  if (!template) {
    return null;
  }

  // First render the template with Mustache
  const renderedText = Mustache.render(template, values);

  if (!highlight) {
    // Simple rendering without highlighting
    return (
      <Text {...textProps}>
        {renderedText.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < renderedText.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  // For highlighted rendering, we need to manually highlight each placeholder
  // First, let's identify where each placeholder is in the original template
  const placeholders: Array<{
    placeholder: string;
    start: number;
    end: number;
  }> = [];

  const placeholderRegex = /\{\{(\w+)\}\}/g;
  let match;

  // Find all placeholders in the original template
  while ((match = placeholderRegex.exec(template)) !== null) {
    const placeholder = match[1];
    if (values[placeholder]) {
      placeholders.push({
        placeholder,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  }

  // If no placeholders, just render the text
  if (placeholders.length === 0) {
    return (
      <Text {...textProps}>
        {renderedText.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < renderedText.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  // Sort placeholders by their position in template
  placeholders.sort((a, b) => a.start - b.start);

  // Create segments for rendering
  const segments: React.ReactNode[] = [];
  const currentText = renderedText;
  let currentPosition = 0;

  // Process each placeholder separately and render the complete text
  for (const placeholder of placeholders) {
    const value = values[placeholder.placeholder];

    // Find the value in the rendered text
    const valueIndex = currentText.indexOf(value, currentPosition);

    if (valueIndex !== -1) {
      // Add text before this placeholder value
      if (valueIndex > currentPosition) {
        const beforeText = currentText.substring(currentPosition, valueIndex);
        segments.push(beforeText);
      }

      // Add the highlighted value
      segments.push(
        <Span key={`highlight-${placeholder.placeholder}`} style={highlightStyle}>
          {value}
        </Span>
      );

      // Update current position
      currentPosition = valueIndex + value.length;
    }
  }

  // Add any remaining text
  if (currentPosition < currentText.length) {
    segments.push(currentText.substring(currentPosition));
  }

  return (
    <Text {...textProps}>
      {segments.map((segment, i) => (
        <React.Fragment key={i}>
          {typeof segment === 'string'
            ? segment.split('\n').map((line, j) => (
                <React.Fragment key={j}>
                  {line}
                  {j < segment.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))
            : segment}
        </React.Fragment>
      ))}
    </Text>
  );
};

export default TemplatedText;
